import { FunctionComponent, useCallback } from "react";
import { ExternalIcon } from "@icons/ExternalIcon";
import {
  selectClient,
  selectIsKioskEnabled,
} from "@store/client/clientReducer";
import { selectPlan, selectPlanShareUrl } from "@store/plan/planReducer";
import { useAppSelector } from "@store/store";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { TabButton } from "@ui-kit/TabButton/TabButton";
import { useTranslation } from "react-i18next";
import { Text } from "@ui-kit/Text/Text";
import { ShareCopyButton } from "./ShareCopyButton";
import { Button } from "@ui-kit/Button";
import { createAnalyticsEvent } from "@lib/analytics";
import { eventNames } from "@lib/analyticsEventNames";

interface ShareHomeProps {
  setView: (view: "home" | "sms" | "email" | "help") => void;
  onClose: () => void;
}

const ShareHome: FunctionComponent<ShareHomeProps> = ({ setView, onClose }) => {
  const { t } = useTranslation();
  const plan = useAppSelector(selectPlan)!;
  const shareUrl = useAppSelector(selectPlanShareUrl);
  const client = useAppSelector(selectClient)!;
  const isKioskEnabled = useAppSelector(selectIsKioskEnabled);

  const handleSocialSharePress = useCallback(
    (type: "email" | "sms" | "x" | "instagram" | "facebook") => () => {
      const subject = t("screens.planShare.sharePlanShareTitle", {
        clientName: client.name,
      });

      if (type === "email") {
        setView("email");
      } else if (type === "sms") {
        setView("sms");
      } else if (type === "x") {
        window.open(
          `https://x.com/intent/tweet?url=${encodeURIComponent(
            shareUrl,
          )}&text=${encodeURIComponent(subject)}`,
          "_blank",
          "noopener,noreferrer",
        );
        createAnalyticsEvent(eventNames.sharePlan, {
          type: "x",
        });
      } else if (type === "facebook") {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            shareUrl,
          )}`,
          "_blank",
          "noopener,noreferrer",
        );
        createAnalyticsEvent(eventNames.sharePlan, {
          type: "facebook",
        });
      }
    },
    [t, client.name, setView, shareUrl],
  );

  const handleGetRecommendations = useCallback(() => {
    setView("help");
  }, [setView]);

  return (
    <>
      {!isKioskEnabled && (
        <div className="social-share-buttons">
          <TabButton
            variant="default"
            iconPrefix={
              <ExternalIcon
                svgName="mail"
                size={16}
                color={colors.neutral700}
              />
            }
            label={t("share.email")}
            onPress={handleSocialSharePress("email")}
          />
          <TabButton
            variant="default"
            iconPrefix={
              <ExternalIcon svgName="sms" size={16} color={colors.neutral700} />
            }
            label={t("share.sms")}
            onPress={handleSocialSharePress("sms")}
          />
          <TabButton
            variant="default"
            iconPrefix={
              <ExternalIcon svgName="x" size={16} color={colors.neutral700} />
            }
            label={t("share.x")}
            onPress={handleSocialSharePress("x")}
          />
          <TabButton
            variant="default"
            iconPrefix={
              <ExternalIcon
                svgName="facebook"
                size={16}
                color={colors.neutral700}
              />
            }
            label={t("share.facebook")}
            onPress={handleSocialSharePress("facebook")}
          />
        </div>
      )}

      <div
        className="qr-code-container"
        style={{ backgroundColor: colors.primary10 }}>
        <Text variant="fontBodyMedium">
          {t("screens.planShare.scanQRCode")}
        </Text>
        <img
          src={`/plans/${plan.id}/share.svg`}
          alt="QR Code"
          width={88}
          height={88}
        />
      </div>

      <div className="need-help-container">
        <Text variant="fontHeadingSmall">
          {t("screens.planShare.needHelpTitle")}
        </Text>
        <Text variant="fontBodyMedium">
          {t("screens.planShare.needHelpDescription")}
        </Text>
        <TabButton
          variant="primary"
          onPress={handleGetRecommendations}
          label={t("screens.planShare.getRecommendations")}
        />
      </div>

      <div className="share-plan-actions-container">
        <ShareCopyButton />
        <Button type="primary" onPress={onClose}>
          {t("common.close")}
        </Button>
      </div>
    </>
  );
};

export { ShareHome };
